/**
 * AI Recruit - Markdown Renderer Component
 * Proper markdown renderer for agent analysis outputs using ReactMarkdown + GFM
 */

import React from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

interface MarkdownRendererProps {
  content: string
  className?: string
}

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content, className = '' }) => {
  return (
    <div className={`prose prose-sm prose-gray max-w-none ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ node, ...props }) => (
            <h1 className="text-2xl font-bold text-gray-900 mt-6 mb-3" {...props} />
          ),
          h2: ({ node, ...props }) => (
            <h2 className="text-xl font-bold text-gray-900 mt-5 mb-2" {...props} />
          ),
          h3: ({ node, ...props }) => (
            <h3 className="text-lg font-semibold text-gray-900 mt-4 mb-2" {...props} />
          ),
          p: ({ node, ...props }) => (
            <p className="text-gray-700 leading-relaxed" {...props} />
          ),
          code: (props: any) => {
            if (props.inline) {
              return (
                <code className="bg-gray-100 px-1 py-0.5 rounded text-[0.85em] font-mono" {...props}>
                  {props.children}
                </code>
              )
            }
            return (
              <pre className="bg-gray-900 text-gray-100 p-3 rounded text-sm font-mono overflow-x-auto my-3">
                <code className={props.className} {...props}>{props.children}</code>
              </pre>
            )
          },
          table: ({ node, ...props }) => (
            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-200" {...props} />
            </div>
          ),
          th: ({ node, ...props }) => (
            <th className="bg-gray-50 px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider border-b" {...props} />
          ),
          td: ({ node, ...props }) => (
            <td className="px-3 py-2 text-sm text-gray-700 border-b align-top" {...props} />
          ),
          ul: ({ node, ...props }) => (
            <ul className="list-disc pl-5 space-y-1" {...props} />
          ),
          ol: ({ node, ...props }) => (
            <ol className="list-decimal pl-5 space-y-1" {...props} />
          ),
          blockquote: ({ node, ...props }) => (
            <blockquote className="border-l-4 border-gray-300 pl-3 italic text-gray-700" {...props} />
          ),
          a: ({ node, ...props }) => (
            <a className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer" {...props} />
          )
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  )
}

export default MarkdownRenderer
